<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ConversationLabelSelector from './ConversationLabelSelector.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import PlatformTooltipIcon from 'dashboard/components-next/PlatformTooltipIcon.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);

// Verifica se ai_enabled está habilitada para a conta (settings.ai_enabled)
const currentAccount = computed(
  () => store.getters['accounts/getAccount'](currentAccountId.value) || {}
);
const isToggleAIFeatureEnabled = computed(
  () => currentAccount.value?.settings?.ai_enabled ?? false
);

// v6.4.25: Verifica se as etiquetas devem ficar na sidebar (default false = header)
const labelsInSidebar = computed(
  () => currentAccount.value?.settings?.labels_in_sidebar ?? false
);

// Computed para estado do agent_ai
const isAgentAIEnabled = computed(() => {
  return currentChat.value?.additional_attributes?.agent_ai ?? false;
});

// Handler para toggle do agent_ai
const handleAgentAIToggle = async () => {
  // Guard: verifica se há chat selecionado
  if (!currentChat.value?.id) return;

  const newValue = !isAgentAIEnabled.value;

  try {
    await store.dispatch('toggleAgentAI', {
      conversationId: currentChat.value.id,
      enabled: newValue,
    });
    useAlert(
      newValue
        ? t('CONVERSATION.AGENT_AI_TOGGLE.ENABLED')
        : t('CONVERSATION.AGENT_AI_TOGGLE.DISABLED')
    );
  } catch {
    useAlert(t('CONVERSATION.AGENT_AI_TOGGLE.ERROR'));
  }
};

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  // Schedule message option moved to ContactPanel.vue

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

// v6.4.25: Mobile/PWA detection — esconde PlatformTooltipIcon no mobile/PWA
const isMobileOrPwa = ref(false);
const mobileQuery = typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)') : null;

const checkMobileOrPwa = () => {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
  const isMobile = mobileQuery?.matches ?? false;
  isMobileOrPwa.value = isMobile || isStandalone;
};

onMounted(() => {
  checkMobileOrPwa();
  mobileQuery?.addEventListener?.('change', checkMobileOrPwa);
});

onUnmounted(() => {
  mobileQuery?.removeEventListener?.('change', checkMobileOrPwa);
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <!-- Label Selector (WhatsApp-style) — v6.4.25: só aparece se labels_in_sidebar está desativado -->
    <ConversationLabelSelector v-if="!labelsInSidebar" />

    <!-- Toggle I.A. (only visible if feature is enabled for account) -->
    <div
      v-if="isToggleAIFeatureEnabled"
      v-tooltip.bottom="
        isAgentAIEnabled
          ? $t('CONVERSATION.AGENT_AI_TOGGLE.TOOLTIP_OFF')
          : $t('CONVERSATION.AGENT_AI_TOGGLE.TOOLTIP_ON')
      "
      class="flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer hover:bg-n-alpha-2"
      @click="handleAgentAIToggle"
    >
      <!-- v6.4.25: No mobile/PWA, esconde tooltip icon para economizar espaço -->
      <PlatformTooltipIcon
        v-if="!isMobileOrPwa"
        identifier="conversation_agent_ai_toggle"
      />
      <span class="text-xs font-medium text-n-slate-11">
        {{ $t('CONVERSATION.AGENT_AI_TOGGLE.LABEL') }}
      </span>
      <div class="ai-toggle-fixed">
        <ToggleSwitch :model-value="isAgentAIEnabled" />
      </div>
    </div>

    <ResolveAction
      :conversation-id="currentChat?.id"
      :status="currentChat?.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>

<style scoped>
/* Fixed blue for AI toggle — overrides bg-n-brand which may blend with widget styling */
.ai-toggle-fixed :deep(button[aria-checked='true']) {
  background-color: #3b82f6 !important;
}
</style>
